import { useCallback, useEffect, useState } from "react";
import { extractRolesFromAccount, useAuth } from "@/app/providers/useAuth";
import { Button } from "@/shared/ui/button";
import { toast } from "@/shared/lib/toast";
import { ShieldCheck } from "lucide-react";
import { backendLogin } from "@/services/api/auth";

function getRedirectUrl(): string {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("redirect") || "/home";
}

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "errorCode" in error) {
    const msalError = error as { errorCode: string; errorMessage?: string };

    if (msalError.errorMessage) {
      return msalError.errorMessage;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred during login. Please try again.";
}

export function LoginPage() {
  const { loginPopup, isAuthenticated, roles } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const navigateToRedirect = useCallback(() => {
    const url = getRedirectUrl();
    window.location.assign(url);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if (!roles || roles.length === 0) {
        window.location.assign("/auth/unauthorized");
      } else {
        navigateToRedirect();
      }
    }
  }, [isAuthenticated, roles, navigateToRedirect]);

  const handleSsoLogin = async () => {
    setIsLoading(true);
    try {
      const response = await loginPopup();
      const roleList = extractRolesFromAccount(response?.account ?? null);

      if (!roleList || roleList.length === 0) {
        toast.error(
          "Access denied",
          "Your account does not have an assigned app role."
        );
        window.location.assign("/auth/unauthorized");
        return;
      }

      try {
        await backendLogin();
        navigateToRedirect();
      } catch (loginError) {
        toast.error(
          "Login Failed",
          "An unexpected error occurred during login. Please try again."
        );
        return;
      }
    } catch (error) {
      console.error("Entra SSO login failed:", error);
      const errorMessage = getErrorMessage(error);
      toast.error("Login Failed", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-carbon-950 text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-10%] top-[-10%] h-72 w-72 rounded-full bg-imperial-500/30 blur-3xl" />
        <div className="absolute right-[-10%] top-12 h-72 w-72 rounded-full bg-amber-300/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_80%_0%,rgba(0,44,115,0.25),transparent_35%)]" />
      </div>

      <div className="relative w-full max-w-3xl px-6">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center shadow-lg backdrop-blur">
          <div className="mt-6 flex flex-col items-center gap-4">
            <h1 className="text-3xl font-bold text-white">Login with SSO</h1>
          </div>

          <div className="mt-8 space-y-4">
            <Button
              type="button"
              onClick={handleSsoLogin}
              className="h-12 w-full rounded-2xl text-base font-semibold shadow-[0_18px_40px_-20px_rgba(0,44,115,0.65)]"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? "Connecting..." : "Login with SSO"}
            </Button>

            <div className="flex items-center justify-center gap-2 text-sm text-white/70">
              <ShieldCheck className="h-5 w-5 text-emerald-300" />
              <span>Powered by Microsoft Entra ID.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
